import dayjs from 'dayjs';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { ApplicationDataGridRowProps } from '@/models/Application';
import { UserType } from '@/models/User';
import { ApplicationStatus, ApplicationStatusesText } from '@/utils/constants';

import { UsersContext } from './UsersContext';
import {
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-premium';

interface Props {
  currentApps: ApplicationDataGridRowProps[];
  reviewApps: ApplicationDataGridRowProps[];
  reviewApplicationSortModel: GridSortModel | null;
  reviewApplicationFilterModel: GridFilterModel | null;
  reviewApplicationPaginationModel: GridPaginationModel;
  historicApps: ApplicationDataGridRowProps[];
  historicApplicationSortModel: GridSortModel | null;
  historicApplicationFilterModel: GridFilterModel | null;
  historicApplicationPaginationModel: GridPaginationModel;
  historicApplicationYearSelected: string | null;
  partnerApps: ApplicationDataGridRowProps[] | undefined;
  columnVisibilityModel: GridColumnVisibilityModel;
  updateCurrentApps: (a: ApplicationDataGridRowProps[]) => void;
  updateReviewApps: (a: ApplicationDataGridRowProps[]) => void;
  updateReviewApplicationSortModel: (s: GridSortModel | null) => void;
  updateReviewApplicationFilterModel: (f: GridFilterModel | null) => void;
  updateReviewApplicationPaginationModel: (p: GridPaginationModel) => void;
  updateHistoricApps: (a: ApplicationDataGridRowProps[]) => void;
  updateHistoricApplicationSortModel: (s: GridSortModel | null) => void;
  updateHistoricApplicationFilterModel: (f: GridFilterModel | null) => void;
  updateHistoricApplicationPaginationModel: (p: GridPaginationModel) => void;
  updateHistoricApplicationYearSelected: (y: string | null) => void;
  updatePartnerApps: React.Dispatch<
    React.SetStateAction<ApplicationDataGridRowProps[] | undefined>
  >;
  updateApplicationStatus: (a: ApplicationStatus, appId: number) => void;
}

interface ProviderProps {
  children: React.ReactNode;
}

export const DashboardContext = createContext<Props>({
  currentApps: [],
  reviewApps: [],
  reviewApplicationSortModel: [],
  reviewApplicationFilterModel: { items: [] },
  reviewApplicationPaginationModel: { page: 0, pageSize: 25 },
  historicApps: [],
  historicApplicationSortModel: [],
  historicApplicationFilterModel: { items: [] },
  historicApplicationPaginationModel: { page: 0, pageSize: 25 },
  historicApplicationYearSelected: null,
  partnerApps: [],
  columnVisibilityModel: {},
  updateCurrentApps: () => {},
  updateReviewApps: () => {},
  updateReviewApplicationSortModel: () => {},
  updateReviewApplicationFilterModel: () => {},
  updateReviewApplicationPaginationModel: () => {},
  updateHistoricApps: () => {},
  updateHistoricApplicationSortModel: () => {},
  updateHistoricApplicationFilterModel: () => {},
  updateHistoricApplicationPaginationModel: () => {},
  updateHistoricApplicationYearSelected: () => {},
  updatePartnerApps: () => {},
  updateApplicationStatus: () => {},
});

export const ApplicationProvider = ({ children }: ProviderProps) => {
  const { userType } = useContext(UsersContext);

  const [currentApps, setCurrentApps] = useState<ApplicationDataGridRowProps[]>([]);

  const [reviewApps, setReviewApps] = useState<ApplicationDataGridRowProps[]>([]);

  const [reviewApplicationSortModel, setReviewApplicationSortModel] =
    useState<GridSortModel | null>(null);
  const [reviewApplicationFilterModel, setReviewApplicationFilterModel] =
    useState<GridFilterModel | null>({ items: [] });
  const [reviewApplicationPaginationModel, setReviewApplicationPaginationModel] =
    useState<GridPaginationModel>({ page: 0, pageSize: 25 });

  const [historicApps, setHistoricApps] = useState<ApplicationDataGridRowProps[]>([]);

  const [historicApplicationSortModel, setHistoricApplicationSortModel] =
    useState<GridSortModel | null>(null);
  const [historicApplicationFilterModel, setHistoricApplicationFilterModel] =
    useState<GridFilterModel | null>({ items: [] });
  const [historicApplicationPaginationModel, setHistoricApplicationPaginationModel] =
    useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [historicApplicationYearSelected, setHistoricApplicationYearSelected] = useState<
    string | null
  >(null);

  const [partnerApps, setPartnerApps] = useState<ApplicationDataGridRowProps[] | undefined>(
    undefined
  );

  useEffect(() => {
    if (historicApplicationSortModel == null) {
      setHistoricApplicationSortModel([]);
    }
  }, [historicApplicationSortModel]);

  useEffect(() => {
    if (reviewApplicationSortModel == null) {
      setReviewApplicationSortModel([]);
    }
  }, [reviewApplicationSortModel]);

  const columnVisibilityModel = {
    failedApplicationTransactions: false, // Hidden column
  };

  const updateCurrentApps = useCallback((apps: ApplicationDataGridRowProps[]) => {
    setCurrentApps(apps);
  }, []);

  const updateReviewApps = useCallback((apps: ApplicationDataGridRowProps[]) => {
    setReviewApps(apps);
  }, []);

  const updateReviewApplicationSortModel = useCallback((s: GridSortModel | null) => {
    setReviewApplicationSortModel(s);
  }, []);

  const updateReviewApplicationFilterModel = useCallback((f: GridFilterModel | null) => {
    setReviewApplicationFilterModel(f);
  }, []);

  const updateReviewApplicationPaginationModel = useCallback((p: GridPaginationModel) => {
    setReviewApplicationPaginationModel(p);
  }, []);

  const updateHistoricApps = useCallback((apps: ApplicationDataGridRowProps[]) => {
    setHistoricApps(apps);
  }, []);

  const updateHistoricApplicationSortModel = useCallback((s: GridSortModel | null) => {
    setHistoricApplicationSortModel(s);
  }, []);

  const updateHistoricApplicationFilterModel = useCallback((f: GridFilterModel | null) => {
    setHistoricApplicationFilterModel(f);
  }, []);

  const updateHistoricApplicationPaginationModel = useCallback((p: GridPaginationModel) => {
    setHistoricApplicationPaginationModel(p);
  }, []);

  const updateHistoricApplicationYearSelected = useCallback((y: string | null) => {
    setHistoricApplicationYearSelected(y);
  }, []);

  const updateApplicationStatus = useCallback(
    (applicationStatus: ApplicationStatus, appId: number) => {
      const statusDescription = ApplicationStatusesText[applicationStatus];
      const updateDate = `${dayjs().format('YYYY-MM-DD')}T00:00:00`;

      const ca = [...currentApps];
      const i = ca.map((a) => a.applicationId).indexOf(appId);

      if (i >= 0) {
        ca[i].status = applicationStatus;
        ca[i].statusDescription = statusDescription;
        ca[i].lastUpdatedDate = updateDate;

        if (applicationStatus == ApplicationStatus.ApplicationApproved) {
          ca[i].partnerYearsAwarded = ca[i].partnerYearsAwarded + 1;
        }
        setCurrentApps(ca);
      }

      const ra = [...reviewApps];
      const j = ra.map((a) => a.applicationId).indexOf(appId);

      const reviewStage =
        userType == UserType.RegionalCoordinator
          ? ApplicationStatus.PendingRegionalReview
          : userType == UserType.StateCoordinator
            ? ApplicationStatus.PendingStateReview
            : ApplicationStatus.PendingAdfReview;

      if (applicationStatus != reviewStage && j >= 0) {
        const raf = ra.filter((a) => a.applicationId !== appId);
        setReviewApps(raf);
      } else if (applicationStatus == reviewStage && j < 0 && i >= 0) {
        ra.push(ca[i]);
        setReviewApps(ra);
      }

      const ha = [...historicApps];
      const k = ha.map((a) => a.applicationId).indexOf(appId);

      if (k >= 0) {
        ha[k].status = applicationStatus;
        ha[k].statusDescription = statusDescription;
        ha[k].lastUpdatedDate = updateDate;
        if (applicationStatus == ApplicationStatus.ApplicationApproved) {
          ha[k].partnerYearsAwarded = ha[k].partnerYearsAwarded + 1;
        }
        setHistoricApps(ha);
      }

      const pa = [...(partnerApps ?? [])];
      const l = pa.map((a) => a.applicationId).indexOf(appId);

      if (l >= 0) {
        pa[l].status = applicationStatus;
        pa[l].statusDescription = statusDescription;
        pa[l].lastUpdatedDate = updateDate;

        if (applicationStatus == ApplicationStatus.ApplicationApproved) {
          pa[l].partnerYearsAwarded = pa[l].partnerYearsAwarded + 1;
        }
        setPartnerApps(pa);
      }
    },
    [currentApps, historicApps, partnerApps, reviewApps, userType]
  );

  return (
    <DashboardContext.Provider
      value={{
        currentApps,
        reviewApps,
        reviewApplicationSortModel,
        reviewApplicationFilterModel,
        reviewApplicationPaginationModel,
        historicApps,
        historicApplicationSortModel,
        historicApplicationFilterModel,
        historicApplicationPaginationModel,
        historicApplicationYearSelected,
        partnerApps,
        columnVisibilityModel,
        updateCurrentApps,
        updateReviewApps,
        updateReviewApplicationSortModel,
        updateReviewApplicationFilterModel,
        updateReviewApplicationPaginationModel,
        updateHistoricApps,
        updateHistoricApplicationSortModel,
        updateHistoricApplicationFilterModel,
        updateHistoricApplicationPaginationModel,
        updateHistoricApplicationYearSelected,
        updatePartnerApps: setPartnerApps,
        updateApplicationStatus,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};
